import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { useSelector } from 'react-redux';
import i18next from '../translations/i18n';
import Loader from './Loader';
import { chartTooltipStyle, formatChartDate, formatChartTooltip } from './chartTooltip';

// ECharts renders time-axis labels from its own locale, not from i18n.js.
// Register French month names so the x-axis matches the rest of the page.
const ECHARTS_FR = 'FR';
const CHART_TEXT_COLOR = '#050139';
const NEGATIVE_TEXT_COLOR = '#dc3545';
const SERIES_COLORS = ['#050139', '#2DCCD3'];
echarts.registerLocale(ECHARTS_FR, {
  time: {
    monthAbbr: [
      'Jan',
      'Fev',
      'Mar',
      'Avr',
      'Mai',
      'Juin',
      'Juil',
      'Aout',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
  },
});

const getEchartsLocale = () =>
  i18next.language?.toLowerCase().startsWith('fr') ? ECHARTS_FR : 'EN';

const PerformanceGrowthChart = () => {
  const [chartData, setChartData] = useState({
    fundName: '',
    benchmarkName: '',
    dates: [],
    fundSeries: [],
    bmkSeries: [],
    prefData: [],
  });
  const { selectedPeriodFilteredData } = useSelector(state => state.app);

  useEffect(() => {
    if (selectedPeriodFilteredData && selectedPeriodFilteredData.length > 0) {
      const fundName = selectedPeriodFilteredData.find(d => d.wtTypeID === 1)?.name || '';
      const benchmarkName = selectedPeriodFilteredData.find(d => d.wtTypeID !== 1)?.name || '';
      const prefData = [];

      // Step 1: Build chartData with rebasing using officialLevel
      // Find each series' first available level for rebasing.
      const sortedData = [...selectedPeriodFilteredData].sort(
        (a, b) => new Date(a.cobDate) - new Date(b.cobDate),
      );
      const dates = [...new Set(sortedData.map(d => d.cobDate))];
      const baseFundLevel = sortedData.find(
        d => d.wtTypeID === 1 && d.officialLevel,
      )?.officialLevel;
      const baseBmkLevel = sortedData.find(d => d.wtTypeID !== 1 && d.officialLevel)?.officialLevel;

      dates.forEach(date => {
        const fundData = selectedPeriodFilteredData.find(
          d => d.cobDate === date && d.wtTypeID === 1,
        );
        const bmkData = selectedPeriodFilteredData.find(
          d => d.cobDate === date && d.wtTypeID !== 1,
        );

        const rebasedFund =
          fundData?.officialLevel != null && baseFundLevel
            ? (fundData.officialLevel / baseFundLevel) * 10000
            : null;
        const rebasedBmk =
          bmkData?.officialLevel != null && baseBmkLevel
            ? (bmkData.officialLevel / baseBmkLevel) * 10000
            : null;

        const cumFund = rebasedFund !== null ? rebasedFund / 10000 - 1 : null;
        const cumBmk = rebasedBmk !== null ? rebasedBmk / 10000 - 1 : null;

        prefData.push({
          date,
          rebasedFund,
          rebasedBmk,
          cumFund,
          cumBmk,
        });
      });

      setChartData({
        fundName,
        benchmarkName,
        prefData,
      });
    }
  }, [selectedPeriodFilteredData]);

  const normalizeSeries = data =>
    data.map(([date, level]) => {
      if (level === null) return [date, null];
      const pct = (level / 10000 - 1) * 100;
      return [date, parseFloat(pct.toFixed(1))];
    });

  const series = [
    ...(chartData.fundName && chartData.prefData.some(d => d.rebasedFund !== null)
      ? [
          {
            name: chartData.fundName,
            type: 'line',
            data: normalizeSeries(chartData.prefData.map(d => [d.date, d.rebasedFund])),
            showSymbol: false,
          },
        ]
      : []),
    ...(chartData.benchmarkName && chartData.prefData.some(d => d.rebasedBmk !== null)
      ? [
          {
            name: chartData.benchmarkName,
            type: 'line',
            data: normalizeSeries(chartData.prefData.map(d => [d.date, d.rebasedBmk])),
            showSymbol: false,
          },
        ]
      : []),
  ].map((item, index) => ({ ...item, color: SERIES_COLORS[index] }));

  const option = {
    textStyle: { fontFamily: getComputedStyle(document.body).fontFamily },
    grid: { top: 30, right: 8, bottom: 85, left: 21, containLabel: true },
    tooltip: {
      ...chartTooltipStyle,
      trigger: 'axis',
      formatter: params => formatChartTooltip(params, value => `${Number(value[1]).toFixed(1)}%`),
      axisPointer: {
        type: 'cross',
        label: {
          formatter: params => {
            if (params.axisDimension === 'y') {
              return `${params.value.toFixed(1)}%`;
            }
            return formatChartDate(params.value);
          },
        },
      },
      valueFormatter: value => `${value.toFixed(1)}%`,
      textStyle: {
        fontSize: 16,
        color(value) {
          if (value < 0) {
            return NEGATIVE_TEXT_COLOR;
          }
          return CHART_TEXT_COLOR;
        },
      },
    },
    legend: {
      data: series.map(item => item.name),
      bottom: 0,
      textStyle: { color: CHART_TEXT_COLOR, fontSize: 16 },
    },
    xAxis: {
      type: 'time',
      onZero: false,
      nameLocation: 'middle',
      axisLabel: { color: CHART_TEXT_COLOR, fontSize: 16 },
    },
    yAxis: {
      type: 'value',
      name: i18next.t('PERCENTAGE'),
      nameLocation: 'middle',
      nameGap: 55,
      nameTextStyle: { color: CHART_TEXT_COLOR, fontSize: 16 },
      axisLabel: {
        formatter: value => `${value.toFixed(1)}%`,
        color: value => (value < 0 ? NEGATIVE_TEXT_COLOR : CHART_TEXT_COLOR),
        fontSize: 16,
      },
    },
    series,
    media: [
      {
        query: { maxWidth: 480 },
        option: {
          tooltip: { textStyle: { fontSize: 14 } },
          legend: {
            left: 8,
            orient: 'vertical',
            itemWidth: 12,
            itemHeight: 8,
            textStyle: { fontSize: 14, width: 210, overflow: 'truncate' },
          },
          xAxis: {
            splitNumber: 4,
            axisLabel: { fontSize: 14, hideOverlap: true },
          },
          yAxis: {
            nameTextStyle: { fontSize: 14 },
            axisLabel: { fontSize: 14 },
          },
        },
      },
    ],
  };

  return (
    <div>
      {selectedPeriodFilteredData.length > 0 && (
        <ReactECharts
          option={option}
          notMerge
          lazyUpdate
          onChartReady={() => {}}
          onEvents={() => {}}
          style={{
            width: '100%',
          }}
          opts={{ renderer: 'svg', locale: getEchartsLocale() }}
        />
      )}
      {selectedPeriodFilteredData.length === 0 && <Loader />}
    </div>
  );
};

export default PerformanceGrowthChart;
